import { usePrefersReducedMotion, useToken } from '@chakra-ui/react';
import { WarningItem } from '@texas/components/shared/WarningSummary';
import { useCallback, useEffect, useState } from 'react';

const controlSelector =
  'input:not([type="hidden"]):not(:disabled), select:not(:disabled), textarea:not(:disabled), button:not(:disabled)';
const comboboxSelector =
  '[role="combobox"]:not(:disabled):not([aria-disabled="true"])';
const focusOptions: FocusOptions = { preventScroll: true };
const clearHighlight: Keyframe = {
  outline: '2px solid transparent',
  outlineOffset: '4px',
};
const animationOptions: KeyframeAnimationOptions = {
  duration: 2000,
  easing: 'ease-in-out',
};

export function useWarningNavigation(revealGroup?: (groupId?: string) => void) {
  const [warning, setWarning] = useState<WarningItem | null>(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const [highlightColor] = useToken('colors', ['orange.400']);
  const navigate = useCallback(
    (item: WarningItem) => {
      revealGroup?.(item.groupId);
      setWarning({ ...item });
    },
    [revealGroup],
  );

  useEffect(() => {
    if (!warning?.targetId) return;
    const target = document.getElementById(warning.targetId);
    if (!target) return;
    const control = target.matches(controlSelector)
      ? target
      : target.querySelector<HTMLElement>(comboboxSelector) ??
        target.querySelector<HTMLElement>(controlSelector);
    control?.focus(focusOptions);
    target.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'center',
    });
    const highlight = {
      ...clearHighlight,
      outline: `2px solid ${highlightColor}`,
    };
    const animation = target.animate(
      prefersReducedMotion
        ? [highlight, highlight]
        : [
            clearHighlight,
            highlight,
            clearHighlight,
            highlight,
            clearHighlight,
          ],
      animationOptions,
    );
    return () => animation.cancel();
  }, [warning, prefersReducedMotion, highlightColor]);

  return navigate;
}
